import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {setUniqueId} from '../../screens/auth/login/reducer';
import {isWeb} from '../responsive';

/**
 * Custom hook to extract and manage UNID (Unique ID) from URL parameters
 * Implements tab session locking to prevent cross-tab session manipulation
 */
const useUniqueIdExtractor = () => {
  const dispatch = useDispatch();
  const {currentUser, uniqueId} = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (!isWeb) return;

    const urlParams = new URLSearchParams(window.location.search);
    const urlUnid = urlParams.get('unid');
    const urlToken = urlParams.get('token');

    if (urlToken && urlUnid) {
      if (uniqueId && urlUnid === uniqueId) {
        localStorage.setItem('authToken', urlToken);
        urlParams.delete('token');
        const newSearch = urlParams.toString();
        const newUrl =
          window.location.pathname + (newSearch ? `?${newSearch}` : '');
        window.history.replaceState({}, '', newUrl);
        // Do not return, continue to the rest of the logic
      } else {
        // unid does not match, or no uniqueId in redux, ignore token
        urlParams.delete('token');
        const newSearch = urlParams.toString();
        const newUrl =
          window.location.pathname + (newSearch ? `?${newSearch}` : '');
        window.history.replaceState({}, '', newUrl);
      }
    }

    if (!urlUnid) return;

    if (!currentUser || !uniqueId) {
      if (urlUnid) {
        dispatch(setUniqueId(urlUnid));
      }
      return;
    }

    // if urlUnid is different from redux uniqueId, ignore it and revert URL
    if (urlUnid !== uniqueId) {
      const newUrl =
        window.location.pathname +
        (window.location.search
          ? window.location.search.replace(/unid=[^&]*/, `unid=${uniqueId}`)
          : `?unid=${uniqueId}`);

      window.history.replaceState({}, '', newUrl);
      return;
    }

    // Get or set the locked unid for this tab session
    const sessionKey = 'locked_unid_for_tab';
    let lockedUnid = sessionStorage.getItem(sessionKey);

    if (!lockedUnid) {
      lockedUnid = urlUnid;
      sessionStorage.setItem(sessionKey, lockedUnid);
    }

    // Force URL back to locked unid if changed
    if (urlUnid !== lockedUnid) {
      const newUrl =
        window.location.pathname +
        (window.location.search
          ? window.location.search.replace(/unid=[^&]*/, `unid=${lockedUnid}`)
          : `?unid=${lockedUnid}`);

      window.history.replaceState({}, '', newUrl);
      if (uniqueId !== lockedUnid) {
        dispatch(setUniqueId(lockedUnid));
      }
      window.location.reload();
      return;
    }

    // Update Redux state if needed
    if (uniqueId !== urlUnid) {
      dispatch(setUniqueId(urlUnid));
    }
  }, [uniqueId, currentUser, dispatch]);
};

export default useUniqueIdExtractor;